import {
  getSDK,
  getUserProfileCached,
  invalidateUserDataCache
} from '@symbo-ls/workspace-shared/functions/sdk.js'

async function loadProfile(root) {
  try {
    const profile = await getUserProfileCached()
    root.update({
      accountProfile: profile || null,
      accountError: null,
      accountLoading: false
    })
  } catch (err) {
    root.update({
      accountProfile: null,
      accountError: err?.message || 'Failed to load profile',
      accountLoading: false
    })
  }
}

async function saveProfile(root) {
  const { accountDraft } = root
  if (!accountDraft) return
  root.update({ accountSaving: true, accountSaveError: null, accountSaveOk: false })
  try {
    const sdk = getSDK()
    await sdk.updateUser?.({
      name: accountDraft.name,
      email: accountDraft.email
    })
    invalidateUserDataCache?.()
    root.update({
      accountSaving: false,
      accountSaveOk: true,
      accountProfile: { ...(root.accountProfile || {}), ...accountDraft }
    })
    // Clear success banner after 3 s
    setTimeout(() => root.update({ accountSaveOk: false }), 3000)
  } catch (err) {
    root.update({
      accountSaving: false,
      accountSaveError: err?.message || 'canvas.account.saveFailed'
    })
  }
}

async function changePassword(root) {
  const { accountCurrentPassword, accountNewPassword } = root
  if (!accountCurrentPassword || !accountNewPassword) return
  root.update({ passwordSaving: true, passwordError: null, passwordOk: false })
  try {
    const sdk = getSDK()
    await sdk.updatePassword?.({
      currentPassword: accountCurrentPassword,
      newPassword: accountNewPassword
    })
    root.update({
      passwordSaving: false,
      passwordOk: true,
      accountCurrentPassword: '',
      accountNewPassword: ''
    })
    setTimeout(() => root.update({ passwordOk: false }), 3000)
  } catch (err) {
    root.update({
      passwordSaving: false,
      passwordError: err?.message || 'canvas.account.passwordFailed'
    })
  }
}

const fieldRow = (labelText, inputConfig) => {
  const inputId =
    inputConfig.id ||
    'account-' +
      (inputConfig.autocomplete || labelText)
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9-]/gi, '')
        .toLowerCase()
  return {
    flow: 'y',
    gap: 'V',
    Label: {
      tag: 'label',
      text: labelText,
      fontSize: 'Z',
      color: 'caption',
      fontWeight: '500',
      attr: { for: inputId }
    },
    Input: {
      tag: 'input',
      id: inputId,
      theme: 'field',
      minHeight: 'controlH',
      padding: '0 Z2',
      round: 'radiusControl',
      border: 'hairline solid line',
      color: 'title',
      fontSize: 'Z2',
      width: '100%',
      ...inputConfig
    }
  }
}

export const account = {
  tag: 'main',
  id: 'main-content',
  extends: 'Flex',
  flow: 'y',
  align: 'start center',
  // Single `1fr` shell row — `gridRow: '1'` fills the content track; the legacy
  // `'2'` strands the page in an implicit 0-height row 2. See main.js.
  gridRow: '1',
  gridColumn: '1 / -1',
  minHeight: '0',
  height: '100%',
  overflowY: 'auto',
  background: 'document',
  padding: 'D C',

  metadata: (el) => ({ title: el.call('polyglot', 'canvas.page.account.title') }),

  onRender: (el, s) => {
    if (el.__accountLoaded) return
    el.__accountLoaded = true
    const root = s.root || s
    const sdk = getSDK()
    const token = sdk?.getAuthToken?.()
    if (!token) {
      el.router('/signin', el.getRoot())
      return
    }
    root.update({ accountLoading: true })
    loadProfile(root)
  },

  Container: {
    flow: 'y',
    gap: 'C',
    width: '100%',
    maxWidth: '540px',

    TopBar: {
      flow: 'x',
      align: 'center space-between',
      gap: 'Y',

      BackBtn: {
        tag: 'button',
        type: 'button',
        minHeight: 'controlHCompact',
        padding: '0 Z',
        round: 'radiusPill',
        border: '0',
        background: 'overlay',
        color: 'caption',
        fontSize: 'Z',
        cursor: 'pointer',
        ':hover': { background: 'selected', color: 'title' },
        text: '{{ canvas.account.backToDashboard | polyglot }}',
        onClick: (e, el, s) => el.router('/', el.getRoot())
      }
    },

    PageHeading: {
      flow: 'y',
      gap: 'V',
      H1: {
        // One title per host (workspace design-direction.md §3): "Account" is
        // the PageSlot pane bar word for word, so the heading yields under
        // one. The subtitle says more and stays.
        hide: (el, s) => el.call('paneTitleBarShown', s),
        tag: 'h1',
        text: '{{ canvas.account.heading | polyglot }}',
        fontSize: 'C',
        fontWeight: '700',
        margin: '0',
        color: 'title',
        letterSpacing: '-0.02em'
      },
      Subtitle: {
        tag: 'p',
        text: '{{ canvas.account.subtitle | polyglot }}',
        fontSize: 'Z1',
        color: 'caption',
        margin: '0'
      }
    },

    LoadingState: {
      if: (el, s) => !!s.root?.accountLoading,
      flow: 'y',
      gap: 'Z',
      width: '100%',
      Row_1: { extends: 'SkeletonRow', width: '40%' },
      Row_2: { extends: 'SkeletonRow', width: '100%' },
      Row_3: { extends: 'SkeletonRow', width: '70%' }
    },

    ErrorState: {
      if: (el, s) => !!s.root?.accountError && !s.root?.accountLoading,
      tag: 'p',
      // Key-in-state — see accountSaveError below.
      text: (el, s) => (s.root?.accountError ? el.call('polyglot', s.root.accountError) : ''),
      color: 'red',
      fontSize: 'Z'
    },

    ProfileSection: {
      if: (el, s) => !!s.root?.accountProfile && !s.root?.accountLoading,
      flow: 'y',
      gap: 'B',
      padding: 'B',
      round: 'radiusCard',
      border: 'hairline solid line',
      background: 'overlay',

      ProfileSectionTitle: {
        tag: 'h2',
        text: '{{ canvas.account.profileSection | polyglot }}',
        fontSize: 'A',
        fontWeight: '700',
        margin: '0',
        color: 'title'
      },

      NameRow: {
        ...fieldRow('{{ canvas.account.fullName | polyglot }}', {
          type: 'text',
          autocomplete: 'name',
          placeholder: (el) => el.call('polyglot', 'canvas.account.fullNamePlaceholder'),
          'aria-label': (el) => el.call('polyglot', 'canvas.account.fullName'),
          value: (el, s) => s.root?.accountDraft?.name ?? s.root?.accountProfile?.name ?? '',
          onInput: (e, el, s) => {
            const root = s.root || s
            const draft = root.accountDraft || { ...root.accountProfile }
            root.update({ accountDraft: { ...draft, name: e.target.value } })
          }
        })
      },

      EmailRow: {
        ...fieldRow('{{ canvas.account.emailAddress | polyglot }}', {
          type: 'email',
          autocomplete: 'email',
          // An example address, not copy — glossary rule 6 (source formatting).
          placeholder: 'you@example.com',
          'aria-label': (el) => el.call('polyglot', 'canvas.account.emailAddress'),
          value: (el, s) => s.root?.accountDraft?.email ?? s.root?.accountProfile?.email ?? '',
          onInput: (e, el, s) => {
            const root = s.root || s
            const draft = root.accountDraft || { ...root.accountProfile }
            root.update({ accountDraft: { ...draft, email: e.target.value } })
          }
        })
      },

      SaveFeedback: {
        if: (el, s) => !!s.root?.accountSaveOk,
        tag: 'p',
        text: '{{ canvas.account.changesSaved | polyglot }}',
        color: 'green',
        fontSize: 'Z',
        margin: '0',
        role: 'status'
      },

      SaveError: {
        if: (el, s) => !!s.root?.accountSaveError,
        tag: 'p',
        // Key-in-state — a raw SDK message passes through polyglot unchanged.
        text: (el, s) =>
          s.root?.accountSaveError ? el.call('polyglot', s.root.accountSaveError) : '',
        color: 'red',
        fontSize: 'Z',
        margin: '0',
        role: 'alert'
      },

      SaveBtn: {
        tag: 'button',
        type: 'submit',
        text: (el, s) =>
          el.call(
            'polyglot',
            s.root?.accountSaving ? 'canvas.account.saving' : 'canvas.account.saveChanges'
          ),
        minHeight: 'controlH',
        // §8.4 width floor — 'Saving…' must not resize the button mid-save.
        minWidth: 'E1',
        padding: '0 A',
        round: 'radiusPill',
        border: '0',
        // §1/§6.2 primary = ink capsule (21:1 both themes). White-on-blue
        // measured 3.62:1 — under the 4.5 small-text floor (2026-08-08 walk).
        background: 'highlight',
        color: 'highlight-reversed',
        fontSize: 'Z2',
        fontWeight: '700',
        cursor: 'pointer',
        alignSelf: 'start',
        ':hover': { background: 'blue+8' },
        disabled: (el, s) => !!s.root?.accountSaving,
        onClick: (e, el, s) => saveProfile(s.root || s)
      }
    },

    PasswordSection: {
      if: (el, s) => !!s.root?.accountProfile && !s.root?.accountLoading,
      flow: 'y',
      gap: 'B',
      padding: 'B',
      round: 'radiusCard',
      border: 'hairline solid line',
      background: 'overlay',

      PasswordSectionTitle: {
        tag: 'h2',
        text: '{{ canvas.account.passwordSection | polyglot }}',
        fontSize: 'A',
        fontWeight: '700',
        margin: '0',
        color: 'title'
      },

      CurrentPasswordRow: {
        ...fieldRow('{{ canvas.account.currentPassword | polyglot }}', {
          type: 'password',
          autocomplete: 'current-password',
          placeholder: '••••••••',
          'aria-label': (el) => el.call('polyglot', 'canvas.account.currentPassword'),
          value: (el, s) => s.root?.accountCurrentPassword || '',
          onInput: (e, el, s) => (s.root || s).update({ accountCurrentPassword: e.target.value })
        })
      },

      NewPasswordRow: {
        ...fieldRow('{{ canvas.account.newPassword | polyglot }}', {
          type: 'password',
          autocomplete: 'new-password',
          placeholder: '••••••••',
          'aria-label': (el) => el.call('polyglot', 'canvas.account.newPassword'),
          value: (el, s) => s.root?.accountNewPassword || '',
          onInput: (e, el, s) => (s.root || s).update({ accountNewPassword: e.target.value })
        })
      },

      PasswordFeedback: {
        if: (el, s) => !!s.root?.passwordOk,
        tag: 'p',
        text: '{{ canvas.account.passwordUpdated | polyglot }}',
        color: 'green',
        fontSize: 'Z',
        margin: '0',
        role: 'status'
      },

      PasswordError: {
        if: (el, s) => !!s.root?.passwordError,
        tag: 'p',
        // Key-in-state — see accountSaveError above.
        text: (el, s) => (s.root?.passwordError ? el.call('polyglot', s.root.passwordError) : ''),
        color: 'red',
        fontSize: 'Z',
        margin: '0',
        role: 'alert'
      },

      PasswordBtn: {
        tag: 'button',
        type: 'submit',
        text: (el, s) =>
          el.call(
            'polyglot',
            s.root?.passwordSaving ? 'canvas.account.updating' : 'canvas.account.updatePassword'
          ),
        minHeight: 'controlH',
        padding: '0 A',
        round: 'radiusPill',
        border: 'hairline solid line',
        background: 'overlay',
        color: 'title',
        fontSize: 'Z2',
        fontWeight: '500',
        cursor: 'pointer',
        alignSelf: 'start',
        ':hover': { background: 'selected' },
        disabled: (el, s) => !!s.root?.passwordSaving,
        onClick: (e, el, s) => changePassword(s.root || s)
      }
    },

    SignOutSection: {
      if: (el, s) => !!s.root?.accountProfile && !s.root?.accountLoading,
      flow: 'y',
      gap: 'B',
      padding: 'B',
      round: 'radiusCard',
      border: 'hairline solid line',
      background: 'overlay',

      SignOutTitle: {
        tag: 'h2',
        text: '{{ canvas.account.signOutSection | polyglot }}',
        fontSize: 'A',
        fontWeight: '700',
        margin: '0',
        color: 'title'
      },

      SignOutHint: {
        tag: 'p',
        text: '{{ canvas.account.signOutHint | polyglot }}',
        fontSize: 'Z',
        color: 'caption',
        margin: '0'
      },

      SignOutBtn: {
        tag: 'button',
        type: 'button',
        text: '{{ canvas.account.signOut | polyglot }}',
        minHeight: 'controlH',
        padding: '0 A',
        round: 'radiusPill',
        border: 'hairline solid line',
        background: 'transparent',
        color: 'title',
        fontSize: 'Z2',
        fontWeight: '500',
        cursor: 'pointer',
        alignSelf: 'start',
        ':hover': { background: 'hover', color: 'red' },
        onClick: (e, el, s) => {
          const sdk = getSDK()
          sdk?.logout?.()
          el.router('/signin', el.getRoot())
        }
      }
    }
  }
}
